/**
 * Activity Summary — "Deej's Gazette", the agent-written recap at the top of the
 * Admin Stats Overview tab.
 *
 * Loading is deliberately NON-BLOCKING and out-of-band: the dashboard's own
 * snapshot fetch never waits on this, and generation (tens of seconds of LLM
 * work) happens behind a Deej loader while the tiles below are already usable.
 *
 * The callable is a small state machine — `running` means another admin's
 * request is generating this range right now, so this polls rather than firing a
 * second generation. Range changes are DEBOUNCED: sweeping the range segments
 * must not kick off a run for every range passed through.
 *
 * SECURITY PIN: every field of the Gazette is model-written text over
 * user-authored game titles and handles. It renders as plain React text only —
 * never markdown, never dangerouslySetInnerHTML.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ActivityGazette, ActivityRangeKey, ActivityShippedMerge } from '@djgg/creations';
import { useServices } from '../services/context.js';
import { Mascot } from '../components/Mascot.js';

/** Wait after the last range change before generating (segment-sweep guard). */
const DEBOUNCE_MS = 700;
/** Poll cadence while another caller holds the generation lease. */
const POLL_MS = 3_000;
/**
 * Give up polling after this long and offer a retry rather than spinning.
 *
 * Must outlast the callable's own 540s ceiling: a generation that runs to the
 * server's limit still answers (with a stored error) at 540s, and a client that
 * quit at 150s would report "still generating" for a run that had already
 * finished. Past this the row's 10-min lease is close to reclaimable, so waiting
 * longer only risks watching a second generation start.
 */
const POLL_LIMIT_MS = 570_000;
/**
 * Minimum time the Deej loader stays on screen before the panel resolves.
 *
 * A cached summary returns in well under a second, which flashed the mascot for
 * a few frames and read as a glitch rather than a character. The art is part of
 * the panel, so it gets long enough to actually be seen. Applies to every exit
 * from the loading state, including errors — one rule, no flicker either way.
 */
const MIN_LOADER_MS = 3_000;
/**
 * What a real generation costs, from prod: the two LLM passes measured 68–80s
 * end to end over the last fortnight. The loader's progress bar is paced against
 * this — it is an EXPECTATION, not a measurement of server progress, so the bar
 * eases toward (and never reaches) full rather than claiming a percentage.
 */
const TYPICAL_RUN_MS = 75_000;

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading'; startedAt: number }
  | { kind: 'ready'; gazette: ActivityGazette; meta: SummaryMeta }
  | { kind: 'error'; note: string };

interface SummaryMeta {
  generatedAt: number | null;
  coveredTo: number | null;
  windowCount: number;
  detailWindows: number;
}

const stamp = (time: number | null) =>
  time == null
    ? ''
    : new Date(time).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

/**
 * Callable failures that are the server's FINAL word, not a lost connection.
 *
 * Everything else (a deadline, an aborted request, a 5xx) says nothing about the
 * generation the lease winner started, so the panel polls through it. A refusal
 * or a bad range, on the other hand, will never become a Gazette however long we
 * wait — retrying those would just spin for four minutes before saying so.
 */
const TERMINAL_CALL_CODES = new Set([
  'functions/permission-denied',
  'functions/unauthenticated',
  'functions/invalid-argument',
  'functions/not-found',
]);

const isTerminalCallFailure = (reason: unknown): boolean => {
  const code = (reason as { code?: unknown } | null)?.code;
  return typeof code === 'string' && TERMINAL_CALL_CODES.has(code);
};

const clockText = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * Deej at work, with an honest clock.
 *
 * Everything shown here is measured on THIS page: how long we have been waiting,
 * and how that compares to a typical run. The client cannot see which of the two
 * LLM passes is running, so nothing claims to — the bar is a pacing expectation
 * (asymptotic, never full until the Gazette actually lands) and the copy only
 * ever says what our own clock knows.
 */
function Loader({ startedAt }: { startedAt: number }) {
  const [elapsed, setElapsed] = useState(() => Date.now() - startedAt);
  useEffect(() => {
    setElapsed(Date.now() - startedAt);
    const tick = window.setInterval(() => setElapsed(Date.now() - startedAt), 1_000);
    return () => window.clearInterval(tick);
  }, [startedAt]);

  // Ease toward 92% over a typical run and crawl after that: a bar that hits
  // 100% while the page is still waiting is the lie this is meant to avoid.
  const pct = Math.round(92 * (1 - Math.exp(-elapsed / (TYPICAL_RUN_MS / 2))));
  const late = elapsed > TYPICAL_RUN_MS * 1.5;

  return (
    <div className="astats-gazette__loading">
      <Mascot context="activity-loading" size={220} quip />
      <p className="astats-muted">
        {late
          ? 'Longer than usual — still writing. Deej gets there.'
          : 'Deej is reading the week’s post. This takes a moment.'}
      </p>
      <div
        className="astats-gazette__progress"
        role="progressbar"
        aria-label="Writing the Gazette"
        aria-valuetext={`waiting ${clockText(elapsed)}`}
      >
        <div className="astats-gazette__progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="astats-gazette__progress-note">
        {clockText(elapsed)} · a fresh one usually takes about {clockText(TYPICAL_RUN_MS)}
      </div>
    </div>
  );
}

const prUrl = (pr: number) => `https://github.com/donjon-bsc/djgg/pull/${pr}`;

/**
 * "and 13 more merged this range" — a disclosure over the merges themselves.
 *
 * The count is arithmetic over every qualifying merge, while `rest` is capped by
 * the rollup, so a long range can list fewer rows than it counts. That gap is
 * stated rather than papered over. A payload written before `shippedRest`
 * existed carries none, so the count stays plain text — nothing to expand.
 */
function ShippedMore({ count, rest }: { count: number; rest: ActivityShippedMerge[] }) {
  const [open, setOpen] = useState(false);
  if (rest.length === 0) {
    return <div className="astats-gazette__shipped-more">and {count} more merged this range</div>;
  }
  return (
    <div className="astats-gazette__shipped-more">
      <button
        className="astats-gazette__shipped-toggle"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        and {count} more merged this range {open ? '▴' : '▾'}
      </button>
      {open && (
        <>
          <ul className="astats-gazette__shipped-rest">
            {rest.map((merge) => (
              <li key={merge.pr}>
                <span className="astats-gazette__shipped-kind">{merge.kind}</span>
                <span className="astats-gazette__shipped-rest-title">{merge.title}</span>
                <a
                  className="astats-gazette__shipped-pr"
                  href={prUrl(merge.pr)}
                  target="_blank"
                  rel="noreferrer"
                >
                  #{merge.pr}
                </a>
              </li>
            ))}
          </ul>
          {count > rest.length && (
            <div>{count - rest.length} beyond these were not carried into the summary.</div>
          )}
        </>
      )}
    </div>
  );
}

export function ActivitySummary({
  range,
  rangeLabel,
  /** The dashboard is anchored to the latest window — summaries only describe it. */
  atLatest,
}: {
  range: ActivityRangeKey;
  rangeLabel: string;
  atLatest: boolean;
}) {
  const services = useServices();
  const [state, setState] = useState<ViewState>({ kind: 'idle' });
  // Guards every async continuation: a range change or unmount must not let an
  // in-flight response overwrite the newer range's state.
  const requestId = useRef(0);

  const run = useCallback(
    async (force: boolean) => {
      const id = ++requestId.current;
      const shownAt = Date.now();
      setState({ kind: 'loading', startedAt: shownAt });
      const deadline = shownAt + POLL_LIMIT_MS;

      /**
       * Leave the loading state, but never before the mascot has had its
       * {@link MIN_LOADER_MS}. The staleness guard is re-checked AFTER the hold:
       * the range can change while we are waiting, and a resolved older request
       * must not paint over the newer one.
       */
      const resolve = async (next: ViewState): Promise<void> => {
        const shownFor = Date.now() - shownAt;
        if (shownFor < MIN_LOADER_MS) {
          await new Promise((done) => window.setTimeout(done, MIN_LOADER_MS - shownFor));
        }
        if (id !== requestId.current) return;
        setState(next);
      };

      let sendForce = force;
      for (;;) {
        let result;
        try {
          result = await services.callable.getActivitySummary({ range, force: sendForce });
        } catch (reason) {
          if (id !== requestId.current) return;
          // A failed CALL is not a failed generation. The winner does the work
          // inside its own invocation, so a dropped connection, a sleeping
          // laptop or a client-side deadline leaves a run that is still going
          // under its lease — and the row it writes is exactly what the next
          // poll reads. Giving up here is how a completed 76s generation got
          // reported as "deadline-exceeded" (real incident, 2026-08-03).
          // Only a terminal refusal or the deadline below ends the wait.
          sendForce = false;
          if (isTerminalCallFailure(reason) || Date.now() > deadline) {
            await resolve({
              kind: 'error',
              note: reason instanceof Error ? reason.message : 'Failed to load the summary.',
            });
            return;
          }
          await new Promise((done) => window.setTimeout(done, POLL_MS));
          if (id !== requestId.current) return;
          continue;
        }
        if (id !== requestId.current) return;
        // A forced regenerate is claimed once; polling for its result must not
        // re-force, or each poll would try to start another run.
        sendForce = false;
        if (result.status === 'ready' && result.payload) {
          await resolve({
            kind: 'ready',
            gazette: result.payload,
            meta: {
              generatedAt: result.generatedAt,
              coveredTo: result.coveredTo,
              windowCount: result.windowCount,
              detailWindows: result.detailWindows,
            },
          });
          return;
        }
        if (result.status === 'error') {
          await resolve({
            kind: 'error',
            note: result.errorNote ?? 'The summary could not be written.',
          });
          return;
        }
        if (Date.now() > deadline) {
          await resolve({ kind: 'error', note: 'Still generating — check back in a minute.' });
          return;
        }
        await new Promise((done) => window.setTimeout(done, POLL_MS));
        if (id !== requestId.current) return;
      }
    },
    [range, services],
  );

  useEffect(() => {
    if (!atLatest) {
      requestId.current += 1; // abandon any in-flight poll for the old range
      setState({ kind: 'idle' });
      return;
    }
    const timer = window.setTimeout(() => void run(false), DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      requestId.current += 1;
    };
  }, [atLatest, run]);

  if (!atLatest) {
    return (
      <div className="panel astats-gazette">
        <div className="astats-eyebrow">Deej’s Gazette</div>
        <p className="astats-muted">
          The Gazette covers the most recent window. Set “Window ending” back to Latest to read it.
        </p>
      </div>
    );
  }

  return (
    <div className="panel astats-gazette" data-testid="activity-summary">
      <div className="astats-panel-heading">
        <div>
          <div className="astats-eyebrow">Deej’s Gazette</div>
          <h2 className="panel__title">
            {state.kind === 'ready' ? state.gazette.headline : `Platform activity · ${rangeLabel}`}
          </h2>
        </div>
        <button
          className="astats-window"
          type="button"
          onClick={() => void run(true)}
          disabled={state.kind === 'loading'}
        >
          regenerate ↻
        </button>
      </div>

      {state.kind === 'loading' && <Loader startedAt={state.startedAt} />}

      {state.kind === 'error' && (
        <p className="astats-gazette__error">
          Summary unavailable — {state.note}{' '}
          <button className="astats-gazette__retry" type="button" onClick={() => void run(false)}>
            try again
          </button>
        </p>
      )}

      {state.kind === 'ready' && (
        <>
          {state.gazette.subhead && <p className="astats-gazette__subhead">{state.gazette.subhead}</p>}
          <div className="astats-gazette__beats">
            {state.gazette.beats.map((beat, index) => (
              <div className="astats-gazette__beat" key={`${beat.title}-${index}`}>
                <div className="astats-gazette__emoji" aria-hidden="true">{beat.emoji}</div>
                <div>
                  <div className="astats-gazette__beat-title">{beat.title}</div>
                  <p className="astats-gazette__beat-line">{beat.line}</p>
                  <div className="astats-gazette__metric">{beat.metric}</div>
                </div>
              </div>
            ))}
          </div>
          {(state.gazette.shipped?.length ?? 0) > 0 && (
            <div className="astats-gazette__shipped">
              <Mascot context="activity-shipped" size={120} quip={false} />
              <div className="astats-gazette__shipped-body">
                <div className="astats-eyebrow">Just shipped</div>
                <ul className="astats-gazette__shipped-list">
                  {state.gazette.shipped!.map((item, index) => (
                    <li key={`${item.title}-${index}`}>
                      <span className="astats-gazette__shipped-title">{item.title}</span>
                      <span className="astats-gazette__shipped-meaning">{item.meaning}</span>
                      {item.pr != null && (
                        <a
                          className="astats-gazette__shipped-pr"
                          href={prUrl(item.pr)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          #{item.pr}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
                {(state.gazette.shippedMore ?? 0) > 0 && (
                  <ShippedMore
                    count={state.gazette.shippedMore!}
                    rest={state.gazette.shippedRest ?? []}
                  />
                )}
              </div>
            </div>
          )}

          {state.gazette.awards.length > 0 && (
            <div className="astats-gazette__awards">
              {state.gazette.awards.map((award, index) => (
                <div className="astats-gazette__award" key={`${award.title}-${index}`}>
                  <div className="astats-gazette__award-title">{award.title}</div>
                  <div className="astats-gazette__award-winner">{award.winner}</div>
                  <div className="astats-gazette__metric">{award.metric}</div>
                </div>
              ))}
            </div>
          )}
          {state.gazette.closer && <p className="astats-gazette__closer">{state.gazette.closer}</p>}
          <div className="astats-gazette__stamp">
            {state.meta.generatedAt ? `Written ${stamp(state.meta.generatedAt)}` : 'Written just now'}
            {state.meta.coveredTo ? ` · as of ${stamp(state.meta.coveredTo)}` : ''}
            {` · ${state.meta.windowCount} windows`}
            {state.meta.detailWindows < state.meta.windowCount
              ? ` · ${state.meta.windowCount - state.meta.detailWindows} without named detail`
              : ''}
          </div>
        </>
      )}
    </div>
  );
}
